import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import 'date-fns';
import { useParams } from 'react-router';
import { Badge, Button, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import Dropdown from '@/components/UI/Dropdown/Dropdown';
import InputText from '@/components/UI/InputText/InputText';
import InputUser from '@/components/UI/InputUser/InputUser';
import LabelDisabledByPDRS from '@/components/UI/LabelDisabledByPDRS/LabelDisabledByPDRS';
import Spinner from '@/components/UI/Spinner/Spinner';
import { getNewState } from '@/helpers/campaignValidations';
import { workflowOptions, ADMIN, statusCampaign, CAUSER_APPROVER_CR, CAUSER_PRE_APPROVER_CR, CAUSER_CHECK_APPROVER_CR, INFORMER_CHECK_APPROVER_CR, INFORMER_PRE_APPROVER_CR, INFORMER_APPROVER_CR, approvalWorkflowStatusesList, HELP_AREA, RECIPIENTS, TAB_FORMS_OPTIONS, APPROVER_MIN_REQUIRED_LEVELS, CR_TYPE } from '@/helpers/constants';
import { addCollapsedDataWorkflow, formatSummaryUser, isCampaignRDMP, isFieldDisabledBYPRRD, isVansBeforeFlowRemoval } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useAuth from '@/hooks/useAuth';
import useCheckDisabled from '@/hooks/useCheckDisabled';
import useError from '@/hooks/useError';
import useToastContext from '@/hooks/useToastContext';
import { setCampaignRecipients, setOriginalValues, setRecipientValidations, setCampaignRiskSCERelevancy } from '@/redux/Campaign/campaign.action';
import { getCampaignRecipients, getOriginalValues, getRecipientsValidations, getCampaignInfo } from '@/redux/Campaign/campaign.reducer';
import { setRecipientsForms } from '@/redux/Forms/forms.action';
import { getRecipientsForms } from '@/redux/Forms/forms.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import { setSettingsInfo } from '@/redux/User/user.action';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import { setCampaignWorkflow } from '@/redux/Workflow/workflow.action';
import { getWorkflow } from '@/redux/Workflow/workflow.reducer';
import campaignServices from '@/services/campaignServices';
import usersServices from '@/services/usersServices';
import { CampaignInfo, DynamicValidationObject, E3QMBObject, LanguageValidations, OriginalValues, Recipients as Recipientss, UserSettings, ValidationField, Workflow } from '@/types';
import styles from './Recipients.module.scss';

// E2C -> only complete causerApprover
// E2I_E2C -> first informerApprover and after causerApprover
// E4C_E3C_E2C -> first causerCheckApprover, after causerPreApprover, finally causerApprover
// E4I_E3I_E2I_E2C -> first informerCheckApprover, after informerPreApprover, after informerApprover and finally causerApprover
// E2C_IPV -> only causerApprover

const disabledApprover = {
  causerCheckApprover: {
    E4C_E3C_E2C: { [statusCampaign.IN_CAUSER_PRE_APPROVAL]: true, [statusCampaign.IN_VAN_CAUSER_APPROVAL]: true },
    E2C_IPV: { [statusCampaign.IN_PLANT_VALIDATION]: true },
  },
  causerApprover: {
    E2C_IPV: { [statusCampaign.IN_PLANT_VALIDATION]: true },
  },
  causerPreApprover: {
    E4C_E3C_E2C: { [statusCampaign.IN_VAN_CAUSER_APPROVAL]: true },
    E2C_IPV: { [statusCampaign.IN_PLANT_VALIDATION]: true },
  },
  informerCheckApprover: {
    E4I_E3I_E2I_E2C: {
      [statusCampaign.IN_INFORMER_PRE_APPROVAL]: true,
      [statusCampaign.IN_INFORMER_APPROVAL]: true,
      [statusCampaign.IN_VAN_CAUSER_APPROVAL]: true,
    },
  },
  informerApprover: {
    E2I_E2C: { [statusCampaign.IN_VAN_CAUSER_APPROVAL]: true },
    E4I_E3I_E2I_E2C: { [statusCampaign.IN_VAN_CAUSER_APPROVAL]: true },
  },
  informerPreApprover: {
    E4I_E3I_E2I_E2C: { [statusCampaign.IN_INFORMER_APPROVAL]: true, [statusCampaign.IN_VAN_CAUSER_APPROVAL]: true },
  },
};

const RECIPIENTS_SECTION = 'recipients';

const useStyles = makeStyles(() => ({
  badge: {
    padding: '0 2px 0 0',
  },
  AddCRTab: {
    padding: 10,
    minWidth: 0,
  },
}));

type RecipientsProps = {
  campaignInfo: CampaignInfo;
  recipients: Recipientss;
  setRecipients: (recipients: Recipientss) => void;
  settingsInfo: UserSettings;
  setRiskSCERelevancy: (riskPotentiallyRelevant: boolean) => void;
  originalValues: OriginalValues;
  setOriginValues: (originValues: OriginalValues) => void;
  recipientsValidations: LanguageValidations;
  setRecipValidations: (recipientsValidations: DynamicValidationObject) => void;
  headerLanguage: string;
  setSettings: (settings: UserSettings) => void;
  setForms: (forms: any) => void;
  approvalWorkflowOptions: {
    [key: string]: { EN: string; DE: string };
  };
  setHelpArea: (area: string) => void;
  subCR: boolean;
  setWorkflow: (workflow: any) => void;
  timeZone: string;
  workflow: Workflow;
};

const Recipients: React.FC<RecipientsProps> = (props) => {
  const { campaignInfo, recipients, setRecipients, settingsInfo, setRiskSCERelevancy, originalValues, setOriginValues, recipientsValidations,
    setRecipValidations, headerLanguage, setForms, approvalWorkflowOptions, setHelpArea, subCR, setWorkflow, timeZone, setSettings, workflow } = props;
  const clss = useStyles();
  const { addToast } = useToastContext();
  const authService = useAuth();
  const { t } = useTranslation();
  const { id, version } = useParams<{ id: string; version: string }>();
  const isLoading = !recipients?.id;
  const validations = recipientsValidations?.[headerLanguage] as DynamicValidationObject;
  const checkDisabled = useCheckDisabled(true) || subCR;
  const { handleCriticalError } = useError();
  const twoWay = recipients?.approvalWorkflow === workflowOptions?.E2I_E2C || recipients?.approvalWorkflow === workflowOptions.DYNAMIC
    || recipients?.approvalWorkflow === workflowOptions?.E4I_E3I_E2I_E2C;
  const causerCodes = [campaignInfo.creationUser, recipients?.standIn, recipients?.responsibleRequester, recipients?.informerApprover, recipients?.informerPreApprover, recipients?.informerCheckApprover];
  const informerCodes = [campaignInfo.creationUser, recipients?.standIn, recipients?.responsibleRequester, recipients?.causerApprover, recipients?.causerPreApprover, recipients?.causerCheckApprover];
  const isAdmin = authService?.checkRole(ADMIN);
  const isCreator = campaignInfo.creationUser?.code === authService.userCode;
  const onCopyOriginValues = (content) => {
    const copyValues = {
      values: content,
      hasChange: false,
    };
    setOriginValues(copyValues);
  };
  const inCreation = campaignInfo.status === statusCampaign.IN_CREATION;
  const inRDCommitee = campaignInfo.status === statusCampaign.IN_RD_COMMITTEE;
  const rdCampaignManagerBlockVisible = isCampaignRDMP(campaignInfo.creationUser?.department) && (inCreation || inRDCommitee);
  const rdCampaignManagerMandatory = campaignInfo.version >= 2;
  const isApproverCauserCommentDisabled = checkDisabled || !recipients?.causerApprover?.code;
  const checkRiskPotentiallyRelevant = useApiRequest(campaignServices.getCampaignRiskSCERelevancy);
  const checkE3Conlfict = (e3QMBReplaced) => e3QMBReplaced && campaignInfo.status === statusCampaign.IN_CREATION && isCreator;
  const informersMapping = [INFORMER_APPROVER_CR, INFORMER_PRE_APPROVER_CR, INFORMER_CHECK_APPROVER_CR];
  const causerMapping = [CAUSER_APPROVER_CR, CAUSER_PRE_APPROVER_CR, CAUSER_CHECK_APPROVER_CR];
  const [approverCauser, setApproverCauser] = useState<string[]>([]);
  const [informerApprovers, setInformerApprovers] = useState<string[]>([]);
  const [showDynamicDialog, setShowDynamicDialog] = useState(false);
  const isOldVanCR = useMemo(() => isVansBeforeFlowRemoval(campaignInfo?.type, campaignInfo?.creationDate), [campaignInfo?.type, campaignInfo?.creationDate]);
  const fetchCampaignInfo = useApiRequest(campaignServices.getCampaign);

  const classes = useStyles();

  const reloadCR = () => {
    if (isOldVanCR) {
      checkRiskPotentiallyRelevant(id, version)
        .then((res) => setRiskSCERelevancy(res.riskPotentiallyRelevant))
        .catch(handleCriticalError);
    }
    Promise.all([
      campaignServices.getCampaignRetry(id, RECIPIENTS_SECTION, version, campaignInfo.type, campaignInfo.creationDate),
      campaignServices.getCampaignRetry(id, 'workflows', version),
    ])
      .then((res) => {
        const [recipientsTab, workflow] = res;
        onCopyOriginValues(recipientsTab);
        setRecipients(recipientsTab);
        setWorkflow({
          ...workflow,
          statuses: addCollapsedDataWorkflow(workflow.statuses, t, timeZone),
          historicalStatuses: addCollapsedDataWorkflow(workflow.historicalStatuses || [], t, timeZone),
        });

        if (isOldVanCR && checkE3Conlfict(res[0].e3QMBReplaced)) {
          addToast(t('validations.e3QMBReplaced'), 'Warning');
        }
      })
      .catch(handleCriticalError);
  };

  const formatLabel = (label) => label.toLowerCase().split('_').map((w, i) => i === 0 ? w : w.charAt(0).toUpperCase() + w.slice(1)).join('');

  const checkEmpty = (list) => {
    return list.every((item) => {
      const key = formatLabel(item);

      return !recipients[key]?.code;
    });
  };

  const setCausers = () => {
    if (checkEmpty(causerMapping)) {
      setApproverCauser([causerMapping[0]]);
    } else {
      let lastWithCode = causerMapping.length - 1;

      for (; lastWithCode >= 0; lastWithCode--) {
        const key = formatLabel(causerMapping[lastWithCode]);
        if (recipients[key]?.code) break;
      }
      setApproverCauser(lastWithCode < 0 ? [causerMapping[0]] : causerMapping.slice(0, lastWithCode + 1));
    };
  };

  const setInformers = () => {
    if (checkEmpty(informersMapping)) {
      setInformerApprovers([]);
    } else {
      let lastWithCode = informersMapping.length - 1;

      for (; lastWithCode >= 0; lastWithCode--) {
        const key = formatLabel(informersMapping[lastWithCode]);
        if (recipients[key]?.code) break;
      }
      setInformerApprovers(lastWithCode < 0 ? [] : informersMapping.slice(0, lastWithCode + 1));
    }
  };

  useEffect(() => {
    setInformerApprovers([]);
    setApproverCauser([]);

    if (recipients?.approvalWorkflow === workflowOptions.DYNAMIC) {
      setCausers();
      setInformers();
    } else {
      setApproverCauser(causerMapping);
      setInformerApprovers(informersMapping);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipients?.approvalWorkflow]);

  useEffect(() => {
    const getTabForms = async () => {
      const updatedCampaignInfo = await fetchCampaignInfo(id, '', version);
      campaignServices.getTabForms(
        updatedCampaignInfo?.type === CR_TYPE.CAR ? TAB_FORMS_OPTIONS.CAMP_RECIPIENTS : TAB_FORMS_OPTIONS.CAMP_VAN_RECIPIENTS,
        campaignInfo?.creationDate,
      ).then((res) => {
        setForms(res);
      }).catch(handleCriticalError);
    };

    if (subCR) {
      campaignServices.getSubCampaign(id, `${isOldVanCR ? 'van_' : ''}recipients`, version)
        .then((data) => {
          onCopyOriginValues(data?.module);
          setRecipients(data?.module);
        })
        .catch(handleCriticalError);
    } else {
      reloadCR();
    }

    getTabForms();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const manageLabel = (causer) => t(`recipients.${formatLabel(causer)}`);

  const manageMandatoryCauser = (label) => {
    if (recipients?.approvalWorkflow === workflowOptions.E4C_E3C_E2C || label === CAUSER_APPROVER_CR) {
      return true;
    }

    return false;
  };

  const manageMandatoryInformer = (label) => {
    if (([workflowOptions.E2I_E2C].includes(recipients?.approvalWorkflow) && label === INFORMER_APPROVER_CR) || [workflowOptions.E4I_E3I_E2I_E2C].includes(recipients?.approvalWorkflow)) {
      return true;
    }

    return false;
  };

  const onSetOriginValues = (hasChange = true) => {
    const values = {
      ...originalValues,
      hasChange,
    };
    setOriginValues(values);
  };

  const validateField = (label, value, plantNumber = null) => {
    const fields = [RECIPIENTS.APPROVAL_WORKFLOW, RECIPIENTS.RESPONSIBLE_REQUESTER, RECIPIENTS.STAND_IN, RECIPIENTS.CAUSER_APPROVER, 'informerApprover',
      RECIPIENTS.CAUSER_PRE_APPROVER, 'informerPreApprover', 'causerCheckApprover', RECIPIENTS.INFORMER_CHECK_APPROVER, 'commentsRdCampaignManager',
      RECIPIENTS.E3_CHIEF_ENGINEER];

    if (fields.includes(label)) {
      const newState = {
        ...recipientsValidations,
        [headerLanguage]: getNewState(validations, label, value),
      };
      setRecipValidations(newState);
    } else if (label === RECIPIENTS.E3_QMB_APPROVER && plantNumber) {
      const newState = {
        ...recipientsValidations,
        [headerLanguage]: {
          ...validations,
          e3QMBApprover: {
            ...validations?.e3QMBApprover,
            [plantNumber]: getNewState(validations?.e3QMBApprover?.[plantNumber], label, value),
          },
        },
      };
      setRecipValidations(newState as DynamicValidationObject);
    }
  };

  const saveCR = useApiRequest(campaignServices.saveCampaign);
  const refreshSettings = (newSettings) => usersServices.saveUsersSettings(authService.userCode, newSettings);

  const updateState = (label, value, plantNumber = '') => {
    let newState = {
      ...recipients,
      [label]: value,
    };

    if (label === RECIPIENTS.E3_QMB_APPROVER) {
      newState = {
        ...recipients,
        [label]: {
          ...recipients?.e3QMBApprover,
          [plantNumber]: value,
        },
      };
    }

    if (label === RECIPIENTS.CAUSER_APPROVER && !value?.code) {
      newState.commentsApproverCauser = '';
    }

    if (newState[label] !== '') {
      newState?.approvalWorkflowStatuses.push(approvalWorkflowStatusesList[label]);
    } else {
      newState.approvalWorkflowStatuses = newState?.approvalWorkflowStatuses.filter((status) => status !== approvalWorkflowStatusesList[label]);
    }
    saveCR(id, newState, RECIPIENTS_SECTION, campaignInfo.type, campaignInfo.creationDate)
      .then(() => {
        validateField(label, value?.code);
        onSetOriginValues(false);
        setRecipients(newState);
        reloadCR();
      }).catch((error) => addToast(error.message, 'ERROR'));
  };

  const handleSelectWorkflow = async (value) => {
    let newSettings = { ...settingsInfo, workflowSelected: value };

    if (value === workflowOptions.DYNAMIC && !settingsInfo.dynamicWFDialogShown) {
      setShowDynamicDialog(true);
      newSettings = { ...settingsInfo, workflowSelected: value, dynamicWFDialogShown: true };
      setSettings(newSettings);
    }
    const wfApproval = { approvalWorkflow: value };
    campaignServices.updateCampaignRecipients(id, wfApproval, 'approval-workflow', campaignInfo.type, campaignInfo.creationDate)
      .then(() => {
        reloadCR();
        refreshSettings(newSettings);
      })
      .catch((error) => addToast(error.message, 'ERROR'));
  };

  const updateUser = (label, userSelected, plantNumber = '') => {
    const newPerson = {
      code: userSelected?.selected ? userSelected?.userCode : '',
      department: userSelected?.selected ? userSelected.department : '',
      summary: userSelected?.selected ? formatSummaryUser(userSelected) : '',
    };
    updateState(label, newPerson, plantNumber);
  };

  const updateStateComment = (label, event) => {
    const newState = {
      ...recipients,
      [label]: event,
    };
    onSetOriginValues();
    setRecipients(newState);
  };

  let showCommentInformer = false;
  let showCommentCauser = false;

  if (recipients?.id && !subCR) {
    showCommentCauser = informerCodes.some((user) => (user?.code === settingsInfo.userId)) || isAdmin;
    showCommentInformer = causerCodes.some((user) => (user?.code === settingsInfo.userId)) || isAdmin;
  }

  const approvalWorkflowOptionsSorted = React.useMemo(() => {
    const aux = {};

    if (approvalWorkflowOptions) {
      Object.entries(approvalWorkflowOptions)?.forEach(([key, value]) => {
        if (key !== workflowOptions.DYNAMIC) {
          aux[key] = value;
        }
      });

      if (!isOldVanCR) {
        aux[workflowOptions.DYNAMIC] = approvalWorkflowOptions[workflowOptions.DYNAMIC];
      }

      return aux;
    }

    return aux;
  }, [approvalWorkflowOptions, isOldVanCR]);

  const approverDisabled = (level) => {
    const { status } = campaignInfo;
    const { approvalWorkflow } = recipients;

    return disabledApprover?.[level]?.[approvalWorkflow]?.[status] ?? false;
  };

  const addApprover = (approverList, isCauser) => {
    const lastApproverCauser = approverList.length;

    if (lastApproverCauser < 3) {
      if (!isCauser) {
        setInformerApprovers([...approverList, informersMapping[lastApproverCauser]]);
      } else {
        setApproverCauser([...approverList, causerMapping[lastApproverCauser]]);
      }
    } else {
      addToast(t('recipients.maxApprovers'), 'Warning');
    }
  };

  const removeApprover = (approverList, isCauser) => {
    if (!isCauser) {
      updateUser(formatLabel(approverList[approverList.length - 1]), null);
      setInformerApprovers(approverList.slice(0, -1));
    } else {
      if (approverList.length > 1) {
        updateUser(formatLabel(approverList[approverList.length - 1]), null);
        setApproverCauser(approverList.slice(0, -1));
      }
    }
  };

  const isInputUserDisabled = (approver: string) => (
    isFieldDisabledBYPRRD(campaignInfo, `recipients.${approver}`) ? true : (isAdmin ? false : checkDisabled || approverDisabled(approver))
  );

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <>
          <AccordionPanel
            title={t('recipients.CRWorkflow')}
            handleClick={() => setHelpArea(HELP_AREA.WORKFLOW)}
          >
            <div className={styles.FlexColumn}>
              <div className={styles.FlexRow}>
                <div className={styles.FourColumns}>
                  <Dropdown
                    id="select-dropdown-workflow"
                    invalid={(validations?.approvalWorkflow as ValidationField)?.errors?.required}
                    fieldPath={[RECIPIENTS_SECTION, RECIPIENTS.APPROVAL_WORKFLOW]}
                    isMandatory
                    label={<LabelDisabledByPDRS campaignInfo={campaignInfo} label={t('recipients.approvalWorkflow')} fieldName="recipients.approvalWorkflow" />}
                    disabled={checkDisabled || !inCreation || isFieldDisabledBYPRRD(campaignInfo, 'recipients.approvalWorkflow')}
                    options={approvalWorkflowOptionsSorted}
                    value={recipients?.approvalWorkflow}
                    handleChange={(event) => { handleSelectWorkflow(event.target.value); }}
                    language={headerLanguage}
                  />
                </div>
              </div>
            </div>
          </AccordionPanel>
          <AccordionPanel
            title={t('recipients.requester')}
            handleClick={() => setHelpArea(HELP_AREA.REQUESTER)}
          >
            <div className={styles.FlexColumn}>
              <div className={styles.FlexRow}>
                <div className={styles.FourColumns}>
                  <InputUser
                    disabled={isInputUserDisabled(RECIPIENTS.RESPONSIBLE_REQUESTER)}
                    invalid={(validations?.responsibleRequester as ValidationField)?.errors?.required}
                    fieldPath={[RECIPIENTS_SECTION, RECIPIENTS.RESPONSIBLE_REQUESTER]}
                    userFieldPath={[RECIPIENTS_SECTION, 'requester', RECIPIENTS.RESPONSIBLE_REQUESTER]}
                    isMandatory
                    id="input-responsible-requester"
                    label={<LabelDisabledByPDRS campaignInfo={campaignInfo} label={t('recipients.responsibleRequester')} fieldName="recipients.responsibleRequester" />}
                    updateUserSelected={(userSelected) => updateUser(RECIPIENTS.RESPONSIBLE_REQUESTER, userSelected)}
                    value={recipients.responsibleRequester.summary}
                    code={recipients.responsibleRequester.code}
                    title={t('recipients.responsibleRequester')}
                    clear={() => { updateUser(RECIPIENTS.RESPONSIBLE_REQUESTER, null); }}
                  />
                </div>
                <div className={styles.FourColumns}>
                  <InputUser
                    disabled={isInputUserDisabled('recipientsStandIn')}
                    invalid={(validations?.standIn as ValidationField)?.errors?.required}
                    fieldPath={[RECIPIENTS_SECTION, RECIPIENTS.STAND_IN]}
                    userFieldPath={[RECIPIENTS_SECTION, 'requester', 'recipientsStandIn']}
                    isMandatory
                    id="input-stand-in"
                    label={<LabelDisabledByPDRS campaignInfo={campaignInfo} label={t('recipients.standIn')} fieldName="recipients.standIn" />}
                    updateUserSelected={(userSelected) => updateUser(RECIPIENTS.STAND_IN, userSelected)}
                    value={recipients?.standIn.summary}
                    code={recipients?.standIn.code}
                    title={t('recipients.standIn')}
                    clear={() => { updateUser(RECIPIENTS.STAND_IN, null); }}
                  />
                </div>
              </div>
            </div>
          </AccordionPanel>
          {!isOldVanCR && (
            <>
              <AccordionPanel title={t('recipients.approverCauser')} handleClick={() => setHelpArea(HELP_AREA.APPROVER_CAUSER)}>
                <div className={styles.RecipientsRow}>
                  <div className={styles.RecipientsAddedRow}>
                    <div className={styles.OneRow}>
                      {[...approverCauser].reverse().map((causer) => (
                        <InputUser
                          key={causer}
                          disabled={isInputUserDisabled(causer)}
                          invalid={manageMandatoryCauser(causer) && ((validations?.[formatLabel(causer)] as ValidationField)?.errors?.required)}
                          fieldPath={[RECIPIENTS_SECTION, formatLabel(causer)]}
                          userFieldPath={[RECIPIENTS_SECTION, 'approverCauser', causer]}
                          isMandatory={manageMandatoryCauser(causer)}
                          id={`input-recipient-${causer}`}
                          label={<LabelDisabledByPDRS campaignInfo={campaignInfo} label={manageLabel(causer)} fieldName={`recipients.${causer}`} />}
                          updateUserSelected={(userSelected) => updateUser(formatLabel(causer), userSelected)}
                          value={recipients[formatLabel(causer)]?.summary}
                          code={recipients[formatLabel(causer)]?.code}
                          title={manageLabel(causer)}
                          clear={() => { updateUser(formatLabel(causer), null); }}
                          level={APPROVER_MIN_REQUIRED_LEVELS[causer]}
                          {...(APPROVER_MIN_REQUIRED_LEVELS[causer] && { isFixedLevel: false })}
                        />
                      ))}
                      {recipients?.approvalWorkflow === workflowOptions.DYNAMIC
                        && (
                          <div className={styles.RecipientsButtons}>
                            <Button
                              classes={{ root: classes.AddCRTab }}
                              color="primary"
                              onClick={() => addApprover(approverCauser, true)}
                              id="add-causer-approver"
                              data-testid="add-causer-approver"
                              disabled={isInputUserDisabled('add-causer-approver') || approverDisabled(approverCauser)}
                            >
                              <IconButton className={styles.IconButtonGray}>
                                <i className="di icon-plus-hinzufuegen-klein" />
                              </IconButton>
                            </Button>
                            {approverCauser.length > 1 && (
                              <Button
                                classes={{ root: classes.AddCRTab }}
                                color="primary"
                                onClick={() => removeApprover(approverCauser, true)}
                                id="remove-causer-approver"
                                data-testid="remove-causer-approver"
                                disabled={isInputUserDisabled('remove-causer-approver') || approverDisabled(approverCauser)}
                              >
                                <IconButton className={styles.IconButtonGray}>
                                  <i className="di icon-muelleimer-loeschen" />
                                </IconButton>
                              </Button>
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                  <div
                    className={styles.TwoColumns}
                    onClick={() => ((isApproverCauserCommentDisabled && !checkDisabled) ? addToast(t('validations.approverCauserRequired'), 'Warning') : null)}
                  >
                    {showCommentCauser && (
                      <InputText
                        id="recipient-comment-approver-causer"
                        label={t('recipients.commentsCauserApprover')}
                        fieldPath={[RECIPIENTS_SECTION, 'approverCauser', 'commentForApprovers']}
                        disabled={isApproverCauserCommentDisabled}
                        multiline
                        rows={4}
                        maxLength={256}
                        handleChange={(ev) => { updateStateComment('commentsApproverCauser', ev.target.value); }}
                        value={recipients.commentsApproverCauser || ''}
                        helperText={[
                          ...((!checkDisabled && recipients.commentsApproverCauser?.length === 256) ? [t('validations.maxLength', { maxLength: 256 })] : []),
                        ]}
                      />
                    )}
                  </div>
                </div>
              </AccordionPanel>

              {twoWay && (
                <AccordionPanel
                  title={t('recipients.approverInformer')}
                  handleClick={() => setHelpArea(HELP_AREA.APPROVER_INFORMER)}
                >
                  <div className={styles.RecipientsRow}>
                    <div className={styles.RecipientsAddedRow}>
                      <div className={styles.OneRow}>
                        {[...informerApprovers].reverse()?.map((informer) => (
                          <InputUser
                            key={informer}
                            disabled={isInputUserDisabled(informer)}
                            invalid={manageMandatoryInformer(informer) && (validations?.[formatLabel(informer)] as ValidationField)?.errors?.required}
                            fieldPath={[RECIPIENTS_SECTION, formatLabel(informer)]}
                            userFieldPath={[RECIPIENTS_SECTION, 'approverCauser', informer]}
                            isMandatory={manageMandatoryInformer(informer)}
                            id={`input-recipient-${informer}`}
                            label={<LabelDisabledByPDRS campaignInfo={campaignInfo} label={manageLabel(informer)} fieldName={`recipients.${informer}`} />}
                            updateUserSelected={(userSelected) => updateUser(formatLabel(informer), userSelected)}
                            value={recipients[formatLabel(informer)]?.summary}
                            code={recipients[formatLabel(informer)]?.code}
                            title={manageLabel(informer)}
                            clear={() => { updateUser(formatLabel(informer), null); }}
                          />
                        ))}
                        {recipients?.approvalWorkflow === workflowOptions.DYNAMIC
                          && (
                            <div className={styles.RecipientsButtons}>
                              <Button
                                classes={{ root: classes.AddCRTab }}
                                color="primary"
                                onClick={() => addApprover(informerApprovers, false)}
                                id="add-informer-approver"
                                data-testid="add-informer-approver"
                                disabled={isInputUserDisabled('add-informer-approver') || approverDisabled(informerApprovers[1])}
                              >
                                <IconButton className={styles.IconButtonGray}>
                                  <i className="di icon-plus-hinzufuegen-klein" />
                                </IconButton>
                              </Button>
                              {informerApprovers.length > 0 && (
                                <Button
                                  classes={{ root: classes.AddCRTab }}
                                  color="primary"
                                  onClick={() => removeApprover(informerApprovers, false)}
                                  id="remove-informer-approver"
                                  data-testid="remove-informer-approver"
                                  disabled={isInputUserDisabled('remove-informer-approver') || approverDisabled(informerApprovers[1])}
                                >
                                  <IconButton className={styles.IconButtonGray}>
                                    <i className="di icon-muelleimer-loeschen" />
                                  </IconButton>
                                </Button>
                              )}
                            </div>
                          )}
                      </div>
                    </div>

                    <div className={styles.TwoColumns}>
                      {showCommentInformer && (
                        <InputText
                          id="recipient-comment-approver-informer"
                          label={t('recipients.commentsInformerApprover')}
                          disabled={checkDisabled}
                          fieldPath={[RECIPIENTS_SECTION, 'approverInformer', 'commentForApprovers']}
                          multiline
                          rows={4}
                          maxLength={256}
                          handleChange={(ev) => { updateStateComment('commentsApproverInformer', ev.target.value); }}
                          value={recipients.commentsApproverInformer || ''}
                          helperText={[
                            ...((recipients.commentsApproverInformer?.length === 256) ? [t('validations.maxLength', { maxLength: 256 })] : []),
                          ]}
                        />
                      )}
                    </div>
                  </div>
                </AccordionPanel>
              )}
            </>
          )}

          {rdCampaignManagerBlockVisible && (
            <>
              <AccordionPanel title={t('comment')}>
                <div className={styles.FlexColumn}>
                  <div className={styles.FlexRow}>
                    <div className={styles.FourColumns}>
                      <InputUser
                        disabled
                        id="input-rd-campaign-manager"
                        label={t('recipients.rdCampaignManager')}
                        value={recipients.rdCampaignManager?.summary}
                        code={recipients.rdCampaignManager?.code}
                        title={t('recipients.rdCampaignManager')}
                        fieldPath={[RECIPIENTS_SECTION, 'rdCampaignManager']}
                        userFieldPath={[RECIPIENTS_SECTION, 'rdCampaignManagerComments', 'rdCampaignManager']}
                      />
                    </div>

                    <div className={styles.TwoColumns}>
                      <InputText
                        disabled={checkDisabled}
                        id="comment-rd-campaign-manager"
                        label={t('recipients.commentsRdCampaignManager')}
                        isMandatory={rdCampaignManagerMandatory}
                        multiline
                        invalid={rdCampaignManagerMandatory && (validations?.commentsRdCampaignManager as ValidationField)?.errors?.required}
                        rows={4}
                        maxLength={256}
                        fieldPath={[RECIPIENTS_SECTION, 'rdCampaignManagerComments', 'comment']}
                        handleChange={(ev) => { updateStateComment('commentsRdCampaignManager', ev.target.value); }}
                        value={recipients.commentsRdCampaignManager || ''}
                        helperText={[
                          ...((recipients.commentsRdCampaignManager?.length === 256) ? [t('validations.maxLength', { maxLength: 256 })] : []),
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </AccordionPanel>
            </>
          )}

          {isOldVanCR && (
            <>
              <AccordionPanel
                title={t('recipients.vans.causerApproval')}
                handleClick={() => setHelpArea(HELP_AREA.APPROVER_CAUSER)}
              >
                <div className={styles.FourColumns}>
                  <InputUser
                    disabled={isInputUserDisabled(RECIPIENTS.INFORMER_CHECK_APPROVER)}
                    invalid={(validations?.informerCheckApprover as ValidationField)?.errors?.required}
                    fieldPath={[RECIPIENTS_SECTION, RECIPIENTS.INFORMER_CHECK_APPROVER]}
                    userFieldPath={[RECIPIENTS_SECTION, 'e4Approver']}
                    isMandatory
                    id="input-e4-approver-informer"
                    label={<LabelDisabledByPDRS campaignInfo={campaignInfo} label="recipients.vans.informerCheckApprover" fieldName="recipients.informerCheckApprover" />}
                    updateUserSelected={(userSelected) => updateUser(RECIPIENTS.INFORMER_CHECK_APPROVER, userSelected)}
                    value={recipients.informerCheckApprover?.summary}
                    code={recipients.informerCheckApprover?.code}
                    title={t('recipients.vans.informerCheckApprover')}
                    clear={() => { updateUser(RECIPIENTS.INFORMER_CHECK_APPROVER, null); }}
                  />

                </div>
                <div className={styles.FourColumns}>
                  <InputUser
                    disabled={isInputUserDisabled('e3Approver')}
                    invalid={(validations?.causerPreApprover as ValidationField)?.errors?.required}
                    fieldPath={[RECIPIENTS_SECTION, RECIPIENTS.CAUSER_PRE_APPROVER]}
                    userFieldPath={[RECIPIENTS_SECTION, 'approverInformer', 'e3Approver']}
                    isMandatory
                    id="input-e3-approver-causer"
                    label={<LabelDisabledByPDRS campaignInfo={campaignInfo} label="recipients.vans.causerPreApprover" fieldName="recipients.e3Approver" />}
                    updateUserSelected={(userSelected) => updateUser(RECIPIENTS.CAUSER_PRE_APPROVER, userSelected)}
                    value={recipients.causerPreApprover?.summary}
                    code={recipients.causerPreApprover?.code}
                    title={t('recipients.vans.causerPreApprover')}
                    clear={() => { updateUser(RECIPIENTS.CAUSER_PRE_APPROVER, null); }}
                  />
                </div>
              </AccordionPanel>
              <AccordionPanel
                title={t('recipients.vans.carlineConfirmation')}
                handleClick={() => setHelpArea(HELP_AREA.APPROVER_INFORMER)}
              >
                <div className={styles.FourColumns}>
                  <InputUser
                    disabled={isInputUserDisabled(RECIPIENTS.E3_CHIEF_ENGINEER)}
                    invalid={(validations?.e3ChiefEngineer as ValidationField)?.errors?.required}
                    fieldPath={[RECIPIENTS_SECTION, RECIPIENTS.E3_CHIEF_ENGINEER]}
                    userFieldPath={[RECIPIENTS_SECTION, 'approverInformer', RECIPIENTS.E3_CHIEF_ENGINEER]}
                    isMandatory
                    id="input-e3-chief-engineer"
                    label={<LabelDisabledByPDRS campaignInfo={campaignInfo} label="recipients.vans.e3ChiefEngineer" fieldName="recipients.e3ChiefEngineer" />}
                    updateUserSelected={(userSelected) => updateUser(RECIPIENTS.E3_CHIEF_ENGINEER, userSelected)}
                    value={recipients.e3ChiefEngineer?.summary}
                    code={recipients.e3ChiefEngineer?.code}
                    title={t('recipients.vans.e3ChiefEngineer')}
                    clear={() => { updateUser(RECIPIENTS.E3_CHIEF_ENGINEER, null); }}
                  />
                </div>
              </AccordionPanel>
              {recipients?.e3QMBApprover && Object.keys(recipients?.e3QMBApprover as E3QMBObject)?.length > 0 && (
                <>
                  <AccordionPanel
                    title={t('recipients.vans.plantApproval')}
                    handleClick={() => setHelpArea(HELP_AREA.PLANTS_APPROVAL_VANS)}
                  >
                    <div className={styles.FourColumns}>
                      {Object.keys(recipients?.e3QMBApprover as E3QMBObject).map((plantNumber) => (
                        <div className={styles.PlantE3Inputs} key={`${plantNumber}-key`}>
                          <div className={styles.BadgeWrapper}>
                            <Badge
                              className={clss.badge}
                              badgeContent={`${plantNumber}`}
                              color="primary"
                              overlap="rectangular"
                              max={9999}
                            >
                              <i className={`di icon-werk ${styles.Icon}`} />
                            </Badge>
                          </div>
                          <InputUser
                            disabled
                            invalid={validations?.e3QMBApprover?.[plantNumber]?.errors?.required}
                            fieldPath={[RECIPIENTS_SECTION, RECIPIENTS.E3_QMB_APPROVER, plantNumber]}
                            userFieldPath={[RECIPIENTS_SECTION, RECIPIENTS.E3_QMB_APPROVER, plantNumber]}
                            isMandatory
                            id={`input-e3-qmb-approver-${plantNumber}`}
                            label={t('recipients.vans.e3QMBApprover', { plantNumber })}
                            updateUserSelected={(userSelected) => updateUser(RECIPIENTS.E3_QMB_APPROVER, userSelected, plantNumber)}
                            value={recipients?.e3QMBApprover?.[plantNumber].summary}
                            code={recipients?.e3QMBApprover?.[plantNumber].code}
                            title={t('recipients.vans.e3QMBApprover', { plantNumber })}
                            clear={() => { updateUser(RECIPIENTS.E3_QMB_APPROVER, null, plantNumber); }}
                          />
                        </div>
                      ))}
                    </div>
                  </AccordionPanel>
                  {campaignInfo.riskPotentiallyRelevant && (
                    <AccordionPanel
                      title={t('recipients.vans.QMBVanInformation')}
                      handleClick={() => setHelpArea(HELP_AREA.QMB_INFORMATION_VANS)}
                    >
                      <div className={styles.FourColumns}>
                        <InputUser
                          disabled
                          fieldPath={[RECIPIENTS_SECTION, RECIPIENTS.E2_QMB_INFORMER]}
                          userFieldPath={[RECIPIENTS_SECTION, 'approverInformer', RECIPIENTS.E2_QMB_INFORMER]}
                          id="input-e2-qmb-informer"
                          label={t('recipients.vans.e2QMBInformer')}
                          updateUserSelected={(userSelected) => updateUser(RECIPIENTS.E2_QMB_INFORMER, userSelected)}
                          value={recipients.e2QMBInformer?.summary}
                          code={recipients.e2QMBInformer?.code}
                          title={t('recipients.vans.e2QMBInformer')}
                          clear={() => { updateUser(RECIPIENTS.E2_QMB_INFORMER, null); }}
                        />
                      </div>
                    </AccordionPanel>
                  )}
                </>
              )}
            </>
          )}
          {showDynamicDialog && (
            <ConfirmDialog
              type="information"
              confirm={() => setShowDynamicDialog(false)}
              message={t('recipients.dynamicWorkflowInfoMessage')}
              confirmBtnText={t('dialog.ok')}
            />
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  campaignInfo: getCampaignInfo(state),
  recipients: getCampaignRecipients(state),
  settingsInfo: getSettingsInfo(state),
  originalValues: getOriginalValues(state),
  recipientsValidations: getRecipientsValidations(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  approvalWorkflowOptions: getRecipientsForms(state)?.form?.approvalWorkflow?.options,
  timeZone: getSettingsInfo(state)?.timezone,
  workflow: getWorkflow(state),
});

const actionCreators = {
  setRecipients: setCampaignRecipients,
  setOriginValues: setOriginalValues,
  setRecipValidations: setRecipientValidations,
  setForms: setRecipientsForms,
  setSettings: setSettingsInfo,
  setHelpArea: setAreaHelp,
  setRiskSCERelevancy: setCampaignRiskSCERelevancy,
  setWorkflow: setCampaignWorkflow,
};

export default connect(mapStateToProps, actionCreators)(Recipients);
